import React, { useRef, useEffect, useState } from "react";
import { formatCurrency } from "../utils/formatUtils";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  LineElement,
  PointElement,
  LineController,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Chart } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  BarController,
  LineController,
  Title,
  Tooltip,
  Legend
);

const CashflowChart = ({
  data,
  allData,
  warnschwelle,
  startingSaldo,
  onWeekClick,
  currentOffset,
  onScroll,
  totalWeeks,
}) => {
  const chartRef = useRef();
  const containerRef = useRef();
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const prepareChartData = () => {
    const labels = data.map((week) => {
      const weekNum = week.weekNumber;
      const startDay = week.weekStart.getDate();
      const startMonth = week.weekStart.getMonth() + 1;
      return `KW${weekNum}\n${startDay}.${startMonth}`;
    });

    // Prüfe ob Prognose-Daten vorhanden sind
    const hasPrognoseData = data.some(week =>
      (week.einnahmenPrognose || 0) > 0 || (week.ausgabenPrognose || 0) > 0
    );

    const hasOverdueData = data.some(week => (week.einnahmenOverdue || 0) > 0);
    const hasUnversendetData = data.some(week => (week.einnahmenUnversendet || 0) > 0);

    const datasets = [
      // Einnahmen mit versendeter Rechnung: voll grün.
      // Fallback auf week.einnahmen, falls Wochen ohne den Split berechnet wurden.
      {
        type: "bar",
        label: hasUnversendetData ? "Einnahmen (versendet)" : "Einnahmen",
        data: data.map((week) => week.einnahmenVersendet ?? week.einnahmen ?? 0),
        backgroundColor: "rgba(34, 197, 94, 0.8)",
        borderColor: "rgb(34, 197, 94)",
        borderWidth: 1,
        stack: "cashflow",
        order: 2,
      },
      // Einnahmen ohne versendete Rechnung: transparenter (zählen für den
      // Cashflow trotzdem voll — reiner Anzeige-Split)
      ...(hasUnversendetData ? [{
        type: "bar",
        label: "Einnahmen (nicht versendet)",
        data: data.map((week) => week.einnahmenUnversendet || 0),
        backgroundColor: "rgba(34, 197, 94, 0.4)",
        borderColor: "rgb(34, 197, 94)",
        borderWidth: 1,
        stack: "cashflow",
        order: 2,
      }] : []),
      // Einnahmen überfällig (nur wenn vorhanden)
      ...(hasOverdueData ? [{
        type: "bar",
        label: "Einnahmen (überfällig)",
        data: data.map((week) => week.einnahmenOverdue || 0),
        backgroundColor: "rgba(251, 146, 60, 0.8)",
        borderColor: "rgb(251, 146, 60)",
        borderWidth: 2,
        stack: "cashflow",
        order: 2,
      }] : []),
      // Einnahmen Prognose (nur wenn vorhanden)
      ...(hasPrognoseData ? [{
        type: "bar",
        label: "Einnahmen (Prognose)",
        data: data.map((week) => week.einnahmenPrognose || 0),
        backgroundColor: "rgba(34, 197, 94, 0.4)",
        borderColor: "rgb(34, 197, 94)",
        borderWidth: 1,
        borderDash: [3, 3],
        stack: "cashflow",
        order: 2,
      }] : []),
      // Ausgaben normal
      {
        type: "bar",
        label: "Ausgaben",
        data: data.map((week) => -(week.ausgaben || 0)),
        backgroundColor: "rgba(239, 68, 68, 0.8)",
        borderColor: "rgb(239, 68, 68)",
        borderWidth: 1,
        stack: "cashflow",
        order: 2,
      },
      // Ausgaben Prognose (nur wenn vorhanden)
      ...(hasPrognoseData ? [{
        type: "bar",
        label: "Ausgaben (Prognose)",
        data: data.map((week) => -(week.ausgabenPrognose || 0)),
        backgroundColor: "rgba(239, 68, 68, 0.4)",
        borderColor: "rgb(239, 68, 68)",
        borderWidth: 1,
        borderDash: [3, 3],
        stack: "cashflow",
        order: 2,
      }] : []),
      // Kumulativer Saldo (blaue Linie)
      {
        type: "line",
        label: "Cashflow",
        data: data.map((week) => week.kumulativ || 0),
        borderColor: "rgb(59, 130, 246)",
        backgroundColor: "rgba(59, 130, 246, 0.1)",
        borderWidth: 3,
        fill: false,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: data.map((week) =>
          (week.kumulativ || 0) < warnschwelle
            ? "rgb(239, 68, 68)"
            : "rgb(59, 130, 246)"
        ),
        pointBorderColor: "#ffffff",
        pointBorderWidth: 2,
        order: 1,
      },
      // Cashflow mit Prognose (nur wenn unterschiedlich)
      ...(hasPrognoseData ? [{
        type: "line",
        label: "Cashflow (inkl. Prognose)",
        data: data.map((week) => week.kumulativMitPrognose || 0),
        borderColor: "rgb(147, 51, 234)",
        borderWidth: 2,
        borderDash: [5, 5],
        fill: false,
        tension: 0.3,
        pointRadius: 3,
        order: 1,
      }] : []),
      // Eigenanteil Cashflow (nur wenn unterschiedlich vom normalen Cashflow)
      ...(data.some(w => w.kumulativEigenanteil != null && w.kumulativEigenanteil !== w.kumulativ) ? [{
        type: "line",
        label: "Eigenanteil",
        data: data.map((week) => week.kumulativEigenanteil ?? week.kumulativ),
        borderColor: "rgb(14, 165, 133)",
        backgroundColor: "rgba(14, 165, 133, 0.1)",
        borderWidth: 2,
        borderDash: [4, 3],
        fill: false,
        tension: 0.3,
        pointRadius: 3,
        pointBackgroundColor: data.map((week) =>
          (week.kumulativEigenanteil ?? week.kumulativ) < warnschwelle
            ? "rgb(239, 68, 68)"
            : "rgb(14, 165, 133)"
        ),
        pointBorderColor: "#ffffff",
        pointBorderWidth: 1,
        order: 1,
      }] : []),
      // Warnschwelle (orange gestrichelte Linie)
      {
        type: "line",
        label: "Warnschwelle",
        data: new Array(data.length).fill(warnschwelle),
        borderColor: "rgb(251, 146, 60)",
        backgroundColor: "rgba(251, 146, 60, 0.1)",
        borderWidth: 2,
        borderDash: [8, 4],
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 0,
        order: 1,
      },
    ];

    return { labels, datasets };
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 300,
    },
    interaction: {
      mode: "index",
      intersect: false,
    },
    onClick: (event, elements) => {
      if (elements.length > 0 && onWeekClick) {
        const elementIndex = elements[0].index;
        onWeekClick(elementIndex);
      }
    },
    plugins: {
      title: {
        display: false,
      },
      legend: {
        display: true,
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyleWidth: 12,
          padding: 10,
          font: { size: 9 },
          color: "rgba(150, 150, 150, 0.9)",
          generateLabels: (chart) => {
            const datasets = chart.data.datasets;
            return datasets.map((ds, i) => {
              const isLine = ds.type === "line";
              return {
                text: ds.label,
                fontColor: "rgba(150, 150, 150, 0.9)",
                fillStyle: isLine ? "transparent" : (ds.backgroundColor || ds.borderColor),
                strokeStyle: ds.borderColor,
                lineWidth: isLine ? 2 : 0,
                lineDash: ds.borderDash || [],
                pointStyle: isLine ? "line" : "rect",
                hidden: !chart.isDatasetVisible(i),
                datasetIndex: i,
              };
            });
          },
        },
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "white",
        bodyColor: "white",
        borderColor: "rgba(255, 255, 255, 0.1)",
        borderWidth: 1,
        callbacks: {
          title: (tooltipItems) => {
            const index = tooltipItems[0].dataIndex;
            const week = data[index];
            return week.label;
          },
          label: (context) => {
            const dataset = context.dataset;
            const value = context.parsed.y;

            // Only show bar values when hovering bars, line values when hovering lines
            // We detect the "primary" hover target from the first element
            if (value === 0) return null;

            const label = dataset.label;
            if (label.includes("Ausgaben")) {
              return `${label}: ${formatCurrency(Math.abs(value))}`;
            }
            return `${label}: ${formatCurrency(value)}`;
          },
          filter: (tooltipItem) => {
            // Filter tooltip items based on what type the user is hovering
            const chart = tooltipItem.chart;
            const activeElements = chart.getActiveElements();
            if (activeElements.length === 0) return true;

            // Determine the type of the element the user is directly hovering
            const hoveredDatasetIndex = activeElements[0].datasetIndex;
            const hoveredDataset = chart.data.datasets[hoveredDatasetIndex];
            const hoveredIsBar = hoveredDataset.type === "bar";

            // Show only matching types
            const currentDataset = tooltipItem.dataset;
            const currentIsBar = currentDataset.type === "bar";

            if (hoveredIsBar) {
              return currentIsBar;
            } else {
              return !currentIsBar;
            }
          },
          afterBody: (tooltipItems) => {
            if (tooltipItems.length === 0) return [];
            const index = tooltipItems[0].dataIndex;
            const week = data[index];

            // Only show extra info when hovering lines
            const chart = tooltipItems[0].chart;
            const activeElements = chart.getActiveElements();
            if (activeElements.length > 0) {
              const hoveredDataset = chart.data.datasets[activeElements[0].datasetIndex];
              if (hoveredDataset.type === "bar") return [];
            }

            const status =
              (week.kumulativ || 0) < warnschwelle
                ? "⚠ Unter Schwelle"
                : "";
            const lines = [];
            if (status) lines.push(status);
            return lines;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          font: { size: 10 },
          callback: function (value, index) {
            const week = data[index];
            if (!week) return "";
            return [
              `KW${week.weekNumber}`,
              `${week.weekStart.getDate()}.${week.weekStart.getMonth() + 1}`,
            ];
          },
        },
      },
      y: {
        type: "linear",
        display: true,
        position: "left",
        title: {
          display: true,
          text: "CHF",
          font: { size: 11 },
        },
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
        ticks: {
          callback: (value) => formatCurrency(value),
          font: { size: 10 },
        },
        // Fixed min/max based on ALL data so axis doesn't jump when scrolling
        min: (() => {
          const source = allData && allData.length > 0 ? allData : data;
          if (!source || source.length === 0) return -100000;
          const minValue = Math.min(
            ...source.map(d => Math.min(d.kumulativ || 0, d.kumulativMitPrognose || 0, d.kumulativEigenanteil ?? d.kumulativ ?? 0, -(d.ausgaben || 0), -(d.ausgabenPrognose || 0)))
          );
          return Math.floor(minValue * 1.1 / 10000) * 10000;
        })(),
        max: (() => {
          const source = allData && allData.length > 0 ? allData : data;
          if (!source || source.length === 0) return 150000;
          const maxValue = Math.max(
            ...source.map(d => Math.max(d.kumulativ || 0, d.kumulativMitPrognose || 0, d.einnahmen || 0, d.einnahmenPrognose || 0))
          );
          return Math.ceil(maxValue * 1.1 / 10000) * 10000;
        })(),
      },
    },
  };

  const chartData = prepareChartData();

  return (
    <div className="cashflow-chart-scroll-container" ref={containerRef}>
      <div
        className="cashflow-chart-wrapper-scroll"
        style={{
          width: containerWidth,
          height: "400px",
        }}
      >
        <Chart ref={chartRef} type="bar" data={chartData} options={options} />
      </div>

      <div className="chart-navigation">
        <div className="nav-buttons">
          <button
            className="nav-btn-chart prev"
            onClick={() => onScroll(currentOffset - 1)}
            disabled={currentOffset === 0}
            title="Vorherige Woche"
          >
            ‹
          </button>

          <div className="week-range">
            {data.length > 0 && (
              <>
                <span className="range-start">KW{data[0].weekNumber}</span>
                <span className="range-separator">-</span>
                <span className="range-end">
                  KW{data[data.length - 1].weekNumber}
                </span>
              </>
            )}
          </div>

          <button
            className="nav-btn-chart next"
            onClick={() => onScroll(currentOffset + 1)}
            disabled={currentOffset >= totalWeeks - data.length}
            title="Nächste Woche"
          >
            ›
          </button>
        </div>

        <div className="quick-nav">
          <button className="btn btn-primary" onClick={() => onScroll(0)}>
            Aktuelle Woche
          </button>
        </div>
      </div>
    </div>
  );
};

export default CashflowChart;
